import { Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import { LOCAL_EN } from '@/config';
import { X, Package, Ticket, DollarSign, Megaphone, Bell } from 'lucide-react';

import useLanguageStore from '@/stores/useLanguageStore';
import { useEffect, useMemo, useRef, useState, UIEvent } from 'react';
import { useFetchNotificationList, useSeenNotification } from '@/hooksApi';
import { useQueryClient } from '@tanstack/react-query';
import { NotificationCounts, PermissionName } from '@/types';
import useScrollToTop from '@/hooks/useScrollToTop';
import usePermission from '@/hooks/usePermission';
import { NotificationItem } from '../NotificationCard';
import { generatePath, useNavigate } from 'react-router-dom';
import { ROUTE } from '@/config';

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  drawerVisible: boolean;
  setDrawerVisible: (value: boolean) => void;
  notificationCount?: NotificationCounts;
}

// ── Tab enum ──────────────────────────────────────────────────────────────────

export enum NotificationTabEnum {
  All = 'all',
  ORDERS = 'order',
  REMITTANCE = 'remittance',
  ANNOUNCEMENT = 'announcement',
  SUPPORT = 'support',
}

/**
 * Maps notification tab types to the permission required to view them.
 * null means no specific permission needed (visible to all company users).
 */
const TAB_PERMISSION_MAP: Record<NotificationTabEnum, PermissionName | null> = {
  [NotificationTabEnum.All]: null,
  [NotificationTabEnum.ORDERS]: PermissionName.ORDER_AND_SHIPMENT,
  [NotificationTabEnum.REMITTANCE]: PermissionName.FINANCE,
  [NotificationTabEnum.ANNOUNCEMENT]: null,
  [NotificationTabEnum.SUPPORT]: null,
};

// ── Category styles ───────────────────────────────────────────────────────────

type CategoryKey = 'order' | 'remittance' | 'support' | 'announcement';

const CATEGORY_STYLES: Record<
  CategoryKey,
  { bg: string; text: string; label: string; Icon: React.ComponentType<{ className?: string }> }
> = {
  order: {
    bg: 'hsla(210,65%,55%,0.12)',
    text: 'hsl(210,60%,42%)',
    label: 'Orders',
    Icon: Package,
  },
  support: {
    bg: 'hsla(35,80%,55%,0.12)',
    text: 'hsl(35,70%,40%)',
    label: 'Support',
    Icon: Ticket,
  },
  remittance: {
    bg: 'hsla(152,60%,48%,0.12)',
    text: 'hsl(152,55%,35%)',
    label: 'Finance',
    Icon: DollarSign,
  },
  announcement: {
    bg: 'hsla(0,0%,60%,0.10)',
    text: 'hsl(0,0%,38%)',
    label: 'Announcements',
    Icon: Megaphone,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function compactRelativeTime(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

const isNavigableType = (type: NotificationTabEnum) =>
  [
    NotificationTabEnum.ORDERS,
    NotificationTabEnum.REMITTANCE,
    NotificationTabEnum.SUPPORT,
  ].includes(type);

// ── NotifItemCard ─────────────────────────────────────────────────────────────

interface NotifItemCardProps {
  type: NotificationTabEnum;
  title?: string;
  description?: string;
  createdAt: string;
  isSeen?: boolean;
  itemUid: string;
  onNavigate: () => void;
}

const NotifItemCard = ({
  type,
  title,
  description,
  createdAt,
  isSeen,
  onNavigate,
}: NotifItemCardProps) => {
  const isUnread = !isSeen;
  const catStyle =
    type !== NotificationTabEnum.All
      ? CATEGORY_STYLES[type as CategoryKey]
      : null;

  const isLink = isNavigableType(type);

  return (
    <div
      onClick={isLink ? onNavigate : undefined}
      className={`relative rounded-2xl border border-solid p-4 transition-all duration-150 active:scale-[0.98] hover:bg-muted/40 ${
        isLink ? 'cursor-pointer' : ''
      }`}
      style={{
        backgroundColor: '#ffffff',
        borderColor: isUnread ? 'rgba(237, 224, 74, 0.5)' : 'rgb(232, 230, 227)',
      }}
    >
      {/* Unread dot */}
      {isUnread && (
        <span
          className='absolute top-3.5 right-3.5 w-2.5 h-2.5 rounded-full'
          style={{ backgroundColor: 'hsl(55, 82%, 61%)' }}
        />
      )}

      {/* Row 1: category badge + title */}
      <div className='flex items-start gap-1 mb-1 pr-4'>
        {catStyle && (
          <span
            className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 mr-1'
            style={{ background: catStyle.bg, color: catStyle.text }}
          >
            <catStyle.Icon className='w-2.5 h-2.5' />
            {catStyle.label}
          </span>
        )}
        <span className='text-[14px] font-bold leading-snug' style={{ color: 'rgb(57, 57, 60)' }}>
          {title}
        </span>
      </div>

      {/* Row 2: body + time */}
      <div className='flex items-end justify-between gap-3 mt-1'>
        <p className='text-[12px] leading-relaxed line-clamp-1' style={{ color: 'rgb(115, 123, 140)' }}>
          {description}
        </p>
        <span className='text-[11px] whitespace-nowrap shrink-0' style={{ color: 'rgb(115, 123, 140)' }}>
          {compactRelativeTime(createdAt)}
        </span>
      </div>
    </div>
  );
};

// ── PillTabBar ─────────────────────────────────────────────────────────────────

interface PillTabItem {
  key: NotificationTabEnum;
  label: string;
  count: number;
}

interface PillTabBarProps {
  tabs: PillTabItem[];
  activeTab: NotificationTabEnum;
  onTabChange: (key: NotificationTabEnum) => void;
}

const PillTabBar = ({ tabs, activeTab, onTabChange }: PillTabBarProps) => (
  <div
    className='flex items-center gap-2 px-4 py-3 overflow-x-auto no-scrollbar shrink-0 border-b border-solid'
    style={{ borderColor: 'rgb(232, 230, 227)' }}
  >
    {tabs.map(({ key, label, count }) => {
      const isActive = activeTab === key;
      return (
        <button
          key={key}
          onClick={() => onTabChange(key)}
          className='flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[13px] font-semibold whitespace-nowrap border border-solid transition-colors shrink-0 focus:outline-none'
          style={
            isActive
              ? {
                  backgroundColor: 'rgb(237, 224, 74)',
                  borderColor: 'rgb(237, 224, 74)',
                  color: 'rgb(57, 57, 60)',
                }
              : {
                  backgroundColor: '#ffffff',
                  borderColor: 'rgb(232, 230, 227)',
                  color: 'rgb(115, 123, 140)',
                }
          }
        >
          {label}
          <span
            className='text-[11px] font-bold px-1.5 py-0.5 rounded-full'
            style={{
              backgroundColor: isActive ? 'rgba(0, 0, 0, 0.1)' : 'rgb(243, 243, 243)',
              color: isActive ? 'rgb(57, 57, 60)' : 'rgb(115, 123, 140)',
            }}
          >
            {count > 99 ? '99+' : count}
          </span>
        </button>
      );
    })}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const NotificationDrawer = ({ notificationCount, drawerVisible, setDrawerVisible }: Props) => {
  const [activeTab, setActiveTab] = useState<NotificationTabEnum>(NotificationTabEnum.All);

  const seenNotificationIdsRef = useRef<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const navigate = useNavigate();

  const { language } = useLanguageStore();
  const { scrollToTop } = useScrollToTop();

  // ── Data fetching ────────────────────────────────────────────────────────────

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingNotifications,
    isFetching: isFetchingNotifications,
  } = useFetchNotificationList(
    drawerVisible,
    activeTab === NotificationTabEnum.All ? undefined : activeTab,
  );

  const isLoading = isFetchingNotifications || isLoadingNotifications;

  const { mutateAsync: seenNotification } = useSeenNotification();

  const allNotifications = data?.pages?.flatMap((page) => page.object.items) ?? [];

  // ── Tab definitions ──────────────────────────────────────────────────────────

  const allTabDefinitions: { key: NotificationTabEnum; title: string; badge: number }[] = [
    {
      key: NotificationTabEnum.All,
      title: String(t('all')),
      badge: notificationCount?.overall_unseen_count ?? 0,
    },
    {
      key: NotificationTabEnum.ORDERS,
      title: CATEGORY_STYLES.order.label,
      badge: notificationCount?.counts_by_type[NotificationTabEnum.ORDERS] ?? 0,
    },
    {
      key: NotificationTabEnum.SUPPORT,
      title: CATEGORY_STYLES.support.label,
      badge: notificationCount?.counts_by_type[NotificationTabEnum.SUPPORT] ?? 0,
    },
    {
      key: NotificationTabEnum.REMITTANCE,
      title: CATEGORY_STYLES.remittance.label,
      badge: notificationCount?.counts_by_type[NotificationTabEnum.REMITTANCE] ?? 0,
    },
    {
      key: NotificationTabEnum.ANNOUNCEMENT,
      title: CATEGORY_STYLES.announcement.label,
      badge: notificationCount?.counts_by_type[NotificationTabEnum.ANNOUNCEMENT] ?? 0,
    },
  ];

  const visibleTabs: PillTabItem[] = useMemo(
    () =>
      allTabDefinitions
        .filter(({ key }) => {
          const requiredPermission = TAB_PERMISSION_MAP[key];
          return requiredPermission === null || hasPermission(requiredPermission);
        })
        .map(({ key, title, badge }) => ({ key, label: title, count: badge })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notificationCount, t, hasPermission],
  );

  const unreadCount = notificationCount?.overall_unseen_count ?? 0;

  // ── Seen / mark-all logic ────────────────────────────────────────────────────

  const handleSeenNotification = async () => {
    if (seenNotificationIdsRef.current.length > 0 && notificationCount) {
      await seenNotification(seenNotificationIdsRef.current);
      queryClient.invalidateQueries(['notification-count']);
    }
  };

  const handleCloseModal = () => {
    setDrawerVisible(false);
    handleSeenNotification();
  };

  const handleSeen = (id: string) => {
    seenNotificationIdsRef.current.push(id);
  };

  const handleMarkAllRead = async () => {
    const unseenIds = allNotifications
      .filter((n) => !n.seen)
      .map((n) => n.id);

    if (unseenIds.length > 0) {
      await seenNotification(unseenIds);
      seenNotificationIdsRef.current = [];
      queryClient.invalidateQueries(['notification-count']);
      queryClient.invalidateQueries(['notification-list']);
    }
  };

  // ── Infinite scroll ──────────────────────────────────────────────────────────

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 50 && !isFetchingNextPage) {
      if (hasNextPage) fetchNextPage();
    }
  };

  // ── Navigation ───────────────────────────────────────────────────────────────

  const buildRedirectPath = (type: NotificationTabEnum, uid: string): string => {
    switch (type) {
      case NotificationTabEnum.ORDERS:
        return `${ROUTE.ORDER_DETAILS}/${uid}`;
      case NotificationTabEnum.REMITTANCE:
        return generatePath(ROUTE.FINANCE_DETAILS, { id: uid });
      case NotificationTabEnum.SUPPORT:
        return generatePath(ROUTE.TICKET_CENTER_DETAILS, { id: uid });
      default:
        return '';
    }
  };

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!drawerVisible) {
      scrollToTop(scrollRef);
      setActiveTab(NotificationTabEnum.All);
    }
  }, [drawerVisible]);

  // Show a loading skeleton while fetching the first page, or while the list
  // still contains stale data from a previous tab (type mismatch guard).
  const isLoadingData =
    isLoading ||
    (allNotifications.length > 0 &&
      activeTab !== NotificationTabEnum.All &&
      allNotifications[0]?.type !== activeTab);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Drawer
      placement={language === LOCAL_EN ? 'right' : 'left'}
      closable={false}
      onClose={handleCloseModal}
      open={drawerVisible}
      width={400}
      rootClassName='sd-scope sd-font'
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' },
        header: { display: 'none' },
        mask: { backgroundColor: 'rgba(0, 0, 0, 0.30)' },
        content: { background: 'hsl(var(--sd-background))' },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div
        className='flex items-center justify-between px-5 py-4 border-b border-solid shrink-0'
        style={{ borderColor: 'rgb(232, 230, 227)' }}
      >
        <div className='flex items-center gap-2'>
          <h2 className='text-[17px] font-bold m-0' style={{ color: 'rgb(57, 57, 60)' }}>
            {String(t('notifications'))}
          </h2>
          {unreadCount > 0 && (
            <span
              className='inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold'
              style={{ backgroundColor: 'hsl(0, 72%, 51%)' }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        <div className='flex items-center gap-3'>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className='text-[12px] font-semibold hover:text-foreground transition-colors bg-transparent border-0 cursor-pointer p-0'
              style={{ color: 'rgb(115, 123, 140)' }}
            >
              {String(t('mark_all_read'))}
            </button>
          )}
          <button
            onClick={handleCloseModal}
            className='w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors bg-transparent border-0 cursor-pointer p-0'
          >
            <X className='w-4 h-4 text-foreground' />
          </button>
        </div>
      </div>

      {/* ── Pill Tabs ────────────────────────────────────────────────────────── */}
      <PillTabBar
        tabs={visibleTabs}
        activeTab={activeTab}
        onTabChange={(key) => {
          scrollToTop(scrollRef);
          setActiveTab(key);
        }}
      />

      {/* ── Notification list ─────────────────────────────────────────────────── */}
      <div
        className='flex-1 overflow-y-auto px-4 py-3 space-y-2'
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {/* Loading state — first page only (no existing items yet) */}
        {isLoadingData && allNotifications.length === 0 && (
          <div className='flex flex-col items-center justify-center h-40 gap-3 text-muted-foreground'>
            <div className='w-5 h-5 border-2 border-solid border-primary border-t-transparent rounded-full animate-spin' />
          </div>
        )}

        {/* Notification cards */}
        {!isLoadingData &&
          allNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              id={notification.id}
              hasSeen={notification.seen}
              onSeen={handleSeen}
            >
              <NotifItemCard
                type={notification.type as NotificationTabEnum}
                createdAt={notification.created_at}
                title={notification.data?.title}
                description={notification.data?.description}
                isSeen={notification.seen}
                itemUid={notification.data?.uid ?? ''}
                onNavigate={() => {
                  const path = buildRedirectPath(
                    notification.type as NotificationTabEnum,
                    notification.data?.uid ?? '',
                  );
                  if (path) {
                    navigate(path);
                    handleCloseModal();
                  }
                }}
              />
            </NotificationItem>
          ))}

        {/* Fetch-next-page spinner (infinite scroll) */}
        {isFetchingNextPage && (
          <div className='flex justify-center my-4'>
            <div className='w-5 h-5 border-2 border-solid border-primary border-t-transparent rounded-full animate-spin' />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && allNotifications.length === 0 && (
          <div className='flex flex-col items-center justify-center h-40 gap-3 text-muted-foreground'>
            <Bell className='w-8 h-8 opacity-30' />
            <p className='text-[13px]'>{String(t('no_notifications'))}</p>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default NotificationDrawer;
